/**
 * Raw Product Form Component
 *
 * Reusable form for creating and editing raw products.
 */

import type { UseFormReturn } from 'react-hook-form';
import { Input, Textarea, Checkbox, Select, RadioCardGroup, Tooltip } from '@/components/ui';
import type { RadioCardOption, SelectOptionGroup } from '@/components/ui';
import { HelpCircle } from 'lucide-react';
import { useFlatCategories } from '../hooks/useCategories';
import { useUnitsByType } from '../hooks/useUnits';
import { AllergensSelect } from './fields/AllergensSelect';
import { MarketType, MarketTypeLabels, type CreateRawProductDto } from '@bstock/shared';

/**
 * Contenu du tooltip explicatif pour le champ isBulk
 */
const BULK_TOOLTIP_CONTENT = (
  <div className="max-w-xs space-y-2 text-left whitespace-normal">
    <p className="font-semibold">Produit en vrac (Bulk)</p>
    <p>
      Cochez cette case pour les produits servis en vrac et dont le dosage
      varie naturellement :
    </p>
    <ul className="list-disc list-inside space-y-1 text-xs">
      <li>Huile de friture (absorption variable)</li>
      <li>Boissons fontaine (dosage machine)</li>
      <li>Cafe en grains (dosage moulin)</li>
      <li>Glace en bac (dosage a la cuillere)</li>
      <li>Sauces en distributeur</li>
    </ul>
    <p className="text-xs opacity-80">
      Ces produits generent des ecarts "normaux" entre theorique et reel.
      Ils seront analyses separement dans le rapport de marge.
    </p>
  </div>
);

// Market type radio options
const MARKET_TYPE_OPTIONS: RadioCardOption<MarketType>[] = [
  { value: MarketType.FOOD, label: MarketTypeLabels[MarketType.FOOD], description: 'Matieres premieres alimentaires', color: 'rgb(var(--color-success-500))' },
  { value: MarketType.FOOD_IND, label: MarketTypeLabels[MarketType.FOOD_IND], description: 'Produits alimentaires industriels', color: 'rgb(var(--color-success-400))' },
  { value: MarketType.NON_FOOD, label: MarketTypeLabels[MarketType.NON_FOOD], description: 'Emballages, consommables', color: 'rgb(var(--color-primary-500))' },
  { value: MarketType.NON_FOOD_IND, label: MarketTypeLabels[MarketType.NON_FOOD_IND], description: 'Consommables industriels', color: 'rgb(var(--color-primary-400))' },
  { value: MarketType.MAINTENANCE, label: MarketTypeLabels[MarketType.MAINTENANCE], description: "Produits d'hygiene et maintenance", color: 'rgb(var(--color-warning-500))' },
];

import { VAT_OPTIONS } from '../constants';

interface RawProductFormProps {
  form: UseFormReturn<CreateRawProductDto & { isActive?: boolean }>;
  mode?: 'create' | 'edit';
}

export function RawProductForm({ form, mode = 'create' }: RawProductFormProps) {
  const { register, formState: { errors }, setValue, watch } = form;

  const { data: categories, isLoading: categoriesLoading } = useFlatCategories();
  const { data: unitsByType, isLoading: unitsLoading } = useUnitsByType();

  // Build tree options for category select
  const categoryTreeOptions = categories?.map((cat) => ({
    value: cat.id,
    label: cat.name,
    depth: cat.depth,
  })) ?? [];

  // Build grouped options for unit select
  const unitGroups: SelectOptionGroup[] = unitsByType ? [
    { label: 'Masse', options: unitsByType.MASS?.map((u) => ({ value: u.id, label: `${u.name} (${u.symbol})` })) ?? [] },
    { label: 'Volume', options: unitsByType.VOLUME?.map((u) => ({ value: u.id, label: `${u.name} (${u.symbol})` })) ?? [] },
    { label: 'Piece', options: unitsByType.PIECE?.map((u) => ({ value: u.id, label: `${u.name} (${u.symbol})` })) ?? [] },
  ] : [];

  return (
    <div className="space-y-6">
      {/* Section: Informations de base */}
      <div className="bg-neutral-50 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-neutral-600 mb-4 uppercase tracking-wide">
          Informations de base
        </h3>

        <div className="space-y-4">
          {/* Nom */}
          <Input
            label="Nom du produit *"
            {...register('name')}
            error={errors.name?.message}
            placeholder="Ex: Boeuf entrecote"
          />

          {/* SKU et Categorie */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="SKU / Code produit *"
              {...register('sku')}
              error={errors.sku?.message}
              placeholder="Ex: VIA-BOE-ENT"
              disabled={mode === 'edit'}
            />

            <Select
              label="Categorie *"
              value={watch('categoryId') ?? ''}
              onChange={(value) => setValue('categoryId', value)}
              treeOptions={categoryTreeOptions}
              placeholder="Selectionner..."
              error={errors.categoryId?.message}
              disabled={categoriesLoading}
            />
          </div>

          {/* Description (optionnel) */}
          <Textarea
            label="Description"
            {...register('description', {
              setValueAs: (v: string) => v === '' ? null : v,
            })}
            placeholder="Description detaillee du produit..."
            rows={3}
          />

          {/* Code-barres (optionnel) */}
          <Input
            label="Code-barres / EAN"
            {...register('barcode', {
              setValueAs: (v: string) => v === '' ? null : v,
            })}
            error={errors.barcode?.message}
            placeholder="Ex: 3760012345678"
          />
        </div>
      </div>

      {/* Section: Classification */}
      <div className="bg-neutral-50 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-neutral-600 mb-4 uppercase tracking-wide">
          Classification
        </h3>

        <RadioCardGroup
          name="marketType"
          options={MARKET_TYPE_OPTIONS}
          value={watch('marketType')}
          onChange={(value) => setValue('marketType', value)}
          label="Type de marche"
          required
          error={errors.marketType?.message}
          disabled={mode === 'edit'}
        />

        {/* Toggle Bulk avec Tooltip */}
        <div className="mt-4 pt-4 border-t border-neutral-200">
          <div className="flex items-center gap-2">
            <Checkbox
              label="Produit en vrac (Bulk)"
              {...register('isBulk')}
            />
            <Tooltip content={BULK_TOOLTIP_CONTENT} position="right" wide>
              <HelpCircle className="w-4 h-4 text-neutral-400 hover:text-neutral-600 cursor-help" />
            </Tooltip>
          </div>
          <p className="text-xs text-neutral-500 mt-1 ml-7">
            Cochez si ce produit est servi en vrac (huile, boissons fontaine, cafe...).
          </p>
        </div>
      </div>

      {/* Section: Unite & TVA */}
      <div className="bg-neutral-50 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-neutral-600 mb-4 uppercase tracking-wide">
          Unite & TVA
        </h3>

        <div className="space-y-4">
          {/* Unite recette */}
          <Select
            label="Unite recette *"
            value={watch('recipeUnitId') ?? ''}
            onChange={(value) => setValue('recipeUnitId', value)}
            groups={unitGroups}
            placeholder="Selectionner une unite..."
            error={errors.recipeUnitId?.message}
            helperText="L'unite utilisee dans les recettes et fiches techniques"
            disabled={unitsLoading}
          />

          {/* TVA */}
          <Select
            label="TVA achat"
            value={(watch('purchaseVatRate') ?? 5.5).toString()}
            onChange={(value) => setValue('purchaseVatRate', parseFloat(value))}
            options={VAT_OPTIONS}
          />
        </div>
      </div>

      {/* Section: Allergenes */}
      <div className="bg-neutral-50 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-neutral-600 mb-4 uppercase tracking-wide">
          Allergenes
        </h3>

        <AllergensSelect
          value={watch('allergens') ?? []}
          onChange={(value) => setValue('allergens', value)}
        />
      </div>

      {/* Section: Seuils de stock */}
      <div className="bg-neutral-50 rounded-xl p-6">
        <h3 className="text-sm font-semibold text-neutral-600 mb-4 uppercase tracking-wide">
          Seuils de stock
        </h3>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Stock minimum"
            type="number"
            {...register('minStock', { valueAsNumber: true })}
            placeholder="Non défini"
            error={errors.minStock?.message}
          />

          <Input
            label="Stock maximum"
            type="number"
            {...register('maxStock', { valueAsNumber: true })}
            placeholder="Non défini"
            error={errors.maxStock?.message}
          />
        </div>
      </div>

      {/* Section: Statut (only for edit mode or if needed) */}
      {mode === 'edit' && (
        <div className="bg-neutral-50 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-neutral-600 mb-4 uppercase tracking-wide">
            Statut
          </h3>

          <Checkbox
            label="Produit actif"
            {...register('isActive')}
          />
        </div>
      )}
    </div>
  );
}
